/**
 * realtime.ts — desktop "signal-to-pull" client for multi-device sync.
 *
 * Connects to the backend `/sync` Socket.IO namespace and, when a PEER device
 * changes the active store, schedules a debounced ordinary sync (the existing
 * CRDT pull/push). This carries NO row data — only a wake signal — so if the
 * socket is down nothing is lost: the existing focus/network triggers still run
 * (realtime-sync-zero-data-loss).
 *
 * Echo suppression: the backend stamps each signal with the originating
 * `sourceDeviceId`; we ignore signals we caused ourselves.
 *
 * Catch-up: on every (re)connect we schedule a sync, in case signals were
 * missed while the socket was disconnected.
 */
import { io, type Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config';

export interface StoreChangeSignal {
    storeId: string;
    tables: string[];
    sourceDeviceId: string | null;
    ts: number;
}

const DEBOUNCE_MS = 1500;

/** True when a signal originated from this same device (an echo to ignore). */
export function isOwnEcho(signal: StoreChangeSignal, ownDeviceId: string | null): boolean {
    return !!ownDeviceId && signal.sourceDeviceId === ownDeviceId;
}

/**
 * A trailing-debounce scheduler. Coalesces a burst of wake signals into a single
 * sync, respecting the server push/pull throttle.
 */
export function createWakeScheduler(onWake: () => void, debounceMs = DEBOUNCE_MS) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return {
        schedule() {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                onWake();
            }, debounceMs);
        },
        cancel() {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        },
    };
}

export interface RealtimeConfig {
    token: string;
    storeId: string;
    deviceId: string | null;
    /** Called (debounced) when a peer change means we should pull. */
    onWake: () => void;
}

/** Injectable socket factory (overridden in tests). */
type IoFactory = typeof io;

let socket: Socket | null = null;
let scheduler: ReturnType<typeof createWakeScheduler> | null = null;
let connectionKey = '';
let currentToken = '';

function keyOf(cfg: RealtimeConfig): string {
    // Token excluded: a token refresh for the same device/store shouldn't churn
    // the connection. Store/device identity is what determines the room.
    return `${cfg.storeId}::${cfg.deviceId ?? ''}`;
}

/**
 * Open (or reuse) the realtime connection for the given store/device. Safe to
 * call repeatedly; only reconnects when the store or device changes.
 *
 * On a token refresh for the SAME store/device we keep the live socket (no
 * churn) but adopt the new credential on `socket.auth`, which Socket.IO re-reads
 * on every (re)connection. Without this, a socket that first connected with an
 * access token would resend that same token forever — so once it expired the
 * gateway would reject every reconnect permanently (realtime-sync-zero-data-loss
 * still holds via focus/network pull, but realtime stays dead until relaunch).
 */
export function connectRealtime(cfg: RealtimeConfig, ioFactory: IoFactory = io): void {
    const key = keyOf(cfg);
    if (socket && connectionKey === key) {
        if (cfg.token !== currentToken) {
            currentToken = cfg.token;
            socket.auth = { token: cfg.token, storeId: cfg.storeId, deviceId: cfg.deviceId };
            // If the server already dropped us for a now-stale token, reconnect
            // immediately so the refreshed credential is used without waiting for
            // the next backoff tick.
            if (socket.disconnected) socket.connect();
        }
        return; // reuse the existing connection for this store/device
    }
    disconnectRealtime();
    connectionKey = key;
    currentToken = cfg.token;

    scheduler = createWakeScheduler(cfg.onWake);

    const s = ioFactory(`${API_BASE_URL}/sync`, {
        transports: ['websocket'],
        auth: { token: cfg.token, storeId: cfg.storeId, deviceId: cfg.deviceId },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        timeout: 8000,
    });

    s.on('store:changed', (signal: StoreChangeSignal) => {
        if (isOwnEcho(signal, cfg.deviceId)) return;
        scheduler?.schedule();
    });

    // Catch-up on (re)connect: we may have missed signals while disconnected.
    s.on('connect', () => {
        scheduler?.schedule();
    });

    s.on('sync:unauthorized', (info: { reason?: string }) => {
        console.warn('[realtime] connection refused by server:', info?.reason);
    });

    s.on('connect_error', (err: Error) => {
        // Non-fatal — reconnection is automatic and the data path is unaffected.
        console.warn('[realtime] connect_error (will retry):', err.message);
    });

    socket = s;
}

/** Tear down the realtime connection (e.g. logout or store/device change). */
export function disconnectRealtime(): void {
    scheduler?.cancel();
    scheduler = null;
    if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        socket = null;
    }
    connectionKey = '';
    currentToken = '';
}
